import { Icon, Skeleton, Tooltip } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import type { SchedulePreviewOpeningHours } from 'lib-shared-types-api';
import { CSSProperties, Fragment, useMemo, useRef } from 'react';
import { useWindowSize } from 'react-use';

import styles from './day.module.scss';

type Props = {
  day: number;
  schedules: SchedulePreviewOpeningHours[];
  isToday?: boolean;
  isDisabled?: boolean;
  isLoading: boolean;
};

type Entry =
  | {
      type: 'holiday';
      item: SchedulePreviewOpeningHours;
    }
  | {
      type: 'conflict' | 'empty';
      item?: never;
    };

type EntryState = {
  entries: Entry[];
  additionalHolidayCount: number;
};

const calendarEntryHeight = 20;
const calendarEntryGap = 2;

const initIntervals = [];

export function Day({
  day,
  isDisabled,
  isLoading,
  isToday,
  schedules,
}: Props): JSX.Element {
  const [schedule] = schedules;
  const intervals = schedule?.intervals || initIntervals;
  const isConflict = schedules.length > 1;
  const isException = schedules.some(
    ({ opening_hour_type: openingHourType }) => openingHourType !== 'regular',
  );
  const workingHoursTooltipContent = useMemo(
    () => (
      <>
        {intervals.map(({ start, end }, index) => (
          <Fragment key={index}>
            {start} - {end}
            {index !== intervals.length - 1 && <br />}
          </Fragment>
        ))}
      </>
    ),
    [intervals],
  );

  const intervalRef = useRef<HTMLUListElement>(null);
  const entryRef = useRef<HTMLUListElement>(null);
  const { current: entry } = entryRef;

  const maxEntries = Math.floor(
    (entry?.clientHeight || 0) / (calendarEntryHeight + calendarEntryGap),
  );

  const { entries, additionalHolidayCount } = useMemo<EntryState>(() => {
    const maxEntriesOfType = maxEntries - 1; // we always need to display at least one interval.
    const state: EntryState = { entries: [], additionalHolidayCount: 0 };
    const holidays = schedules
      .filter(({ opening_hour_type }) => opening_hour_type === 'holiday')
      .map<Entry>((item) => ({
        type: 'holiday',
        item,
      }));

    // if we don't have enough space for both holidays & conflict row
    // we need to cut the amount of holidays we display.
    if (maxEntriesOfType <= holidays.length && isConflict) {
      const cutHolidays = holidays.slice(0, maxEntriesOfType - 1);
      const additionalHolidayCount = holidays.length - cutHolidays.length;

      state.entries.push(...cutHolidays);
      state.additionalHolidayCount = additionalHolidayCount;
    } else {
      state.entries.push(...holidays);
    }

    if (isConflict) {
      state.entries.push({
        type: 'conflict',
      });
    }

    // used for "pushing down" schedules as to where they'd be if there was a holiday displayed.
    if (holidays.length === 0 && !isConflict) {
      state.entries.push({ type: 'empty' });
    }

    return state;
  }, [maxEntries, schedules, isConflict]);

  const maxIntervalCount = maxEntries - entries.length;

  const isSomeIntervalEllipsed = [
    ...(intervalRef.current?.children || []),
  ].some((child) => child.scrollWidth > child.clientWidth);

  // re-render on window resize
  useWindowSize();

  return (
    <div
      className={styles.root}
      style={
        {
          '--calendar-entry-height': `${calendarEntryHeight}px`,
          '--calendar-entry-gap': `${calendarEntryGap}px`,
        } as CSSProperties
      }
    >
      <div
        className={classnames(styles.content, {
          [styles.tertiary]: isDisabled,
        })}
      >
        <h6 className={classnames(styles.heading, { [styles.bold]: isToday })}>
          {day}
          {isException && (
            <Tooltip
              dark
              wrap="span"
              wrapProps={{
                className: styles.tooltip,
              }}
              content="Exception from regular opening hours"
            >
              <Icon name="exclamation" />
            </Tooltip>
          )}
        </h6>
        <ul className={styles.entries} ref={entryRef}>
          {entries.map(({ type, item: { name } = {} }, index) => {
            if (index + 1 > maxEntries) {
              return null;
            }

            switch (type) {
              case 'holiday':
                return (
                  <li key={name || index} className={styles.entry}>
                    <div className={styles.holiday}>
                      <span>{name}</span>
                      {
                        // if there's additional holidays that were cut,
                        // and this is the last item with type 'holiday' in entry list.
                        additionalHolidayCount !== 0 &&
                          entries[index + 1]?.type !== 'holiday' && (
                            <span>+{additionalHolidayCount} more</span>
                          )
                      }
                    </div>
                  </li>
                );
              case 'conflict':
                return (
                  <li key={type} className={styles.entry}>
                    <div className={styles.conflict}>Conflict</div>
                  </li>
                );
              case 'empty':
                return (
                  <li key={type} className={styles.entry}>
                    <div className={classnames(styles.holiday, styles.hidden)}>
                      Empty Spacer
                    </div>
                  </li>
                );
            }
          })}
          <li>
            <Tooltip
              dark
              wrap="div"
              content={workingHoursTooltipContent}
              disabled={
                // we want to show the tooltip if:
                // 1. any of the elements have ellipsis on them (isSomeIntervalEllipsed)
                // or 2. we're not able to show the entire list of schedules (because there's not enough vertical space)
                !isSomeIntervalEllipsed &&
                (maxIntervalCount >= intervals.length ||
                  intervals.length === 0 ||
                  isConflict)
              }
            >
              <ul className={styles.intervals} ref={intervalRef}>
                {intervals.length === 0 && (
                  <li>
                    {schedule && 'Closed'}
                    {!schedule && isLoading && (
                      <Skeleton height="20px" width="100%" animation="wave" />
                    )}
                    {!schedule && !isLoading && 'n.a.'}
                  </li>
                )}
                {isConflict && <li className={styles.workingHours}>tbd</li>}
                {!isConflict &&
                  intervals.map(({ start, end }, index) => {
                    if (index + 1 > maxIntervalCount) {
                      return null;
                    }

                    const isLastItem = index + 1 === intervals.length;
                    const isLastShown = maxIntervalCount === index + 1;
                    const isContentReplaced =
                      isLastShown && intervals.length > maxIntervalCount;

                    return (
                      <li key={index} className={styles.workingHours}>
                        <span>
                          {!isContentReplaced && (
                            <>
                              {start} - {end}
                            </>
                          )}
                          {isLastShown &&
                            (!isLastItem || isContentReplaced) && <>...</>}
                        </span>
                      </li>
                    );
                  })}
              </ul>
            </Tooltip>
          </li>
        </ul>
      </div>
    </div>
  );
}
